import Layout from 'components/Layout';
import ContentDashboard from 'components/ContentDashboard';
import StructuredDataComponent from 'components/StructuredData';
import StructuredData from 'lib/StructuredData/ArticleSummaries';
import { fetchArticleSummaries } from 'components/ContentDashboard/Articles';
import { NEWS_AND_ANALYSIS_URL } from 'lib/ContentGroups';
import useAdSetup from 'lib/hooks/useAdSetup';
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import { useContentGroups } from 'lib/hooks/useGoogleAnalytics';
import breadcrumbStructuredData from 'lib/StructuredData/breadcrumbStructuredData';
import { useTranslation } from 'react-i18next';
import CarGurusAdUnit from 'lib/CarGurusAds/AdUnit';
import { PageType, AgeType, Zone } from 'lib/CarGurusAds/TaxonomySchema';
import { enableContentCaching } from 'lib/cacheControl';
import isIndexable from 'lib/isIndexable';

interface Props {
  articleSummaries: [];
  articleSummariesTotal: number;
  indexable: boolean;
}

function NewsAndAnalysisPage({
  articleSummaries,
  articleSummariesTotal,
  indexable,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const carGurusAdUnit = new CarGurusAdUnit({ zone: Zone.Research });

  useContentGroups({ default: NEWS_AND_ANALYSIS_URL });

  useAdSetup(carGurusAdUnit.toString(), {
    make: null,
    model: null,
    year: null,
    pageType: PageType.Reviews,
    ageType: AgeType.None,
    cpo: false,
    inLine: 1,
    body_style: null,
    category: null,
    app: 'web',
    sellerId: null,
  });

  const structuredData = new StructuredData({ articleSummaries });

  return (
    <>
      <Layout
        title={t(`contentDashboard.meta.pageTitle`)}
        description={t(`contentDashboard.meta.metaDescription`)}
        indexed={indexable}
      >
        <ContentDashboard
          articleSummaries={articleSummaries}
          articleSummariesTotal={articleSummariesTotal}
        />
      </Layout>
      <StructuredDataComponent data={structuredData} />
      <StructuredDataComponent
        data={breadcrumbStructuredData({
          breadcrumbCategory: 'news-and-analysis',
        })}
      />
    </>
  );
}

export async function getServerSideProps({
  req,
  res,
}: GetServerSidePropsContext): Promise<GetServerSidePropsResult<Props>> {
  const bypassCache = true;
  const indexable = isIndexable(req);

  const { articleSummaries, total } = await fetchArticleSummaries(
    { parentCategory: 'NewsAndAnalysis' },
    { bypassCache }
  );

  enableContentCaching(res);

  return {
    props: {
      articleSummaries,
      articleSummariesTotal: total,
      indexable,
    },
  };
}

export default NewsAndAnalysisPage;
